/**
 * Modal Component
 * 
 * Base modal wrapper providing consistent styling and behavior.
 * Supports standard and fullscreen variants.
 */

import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Modal content */
  children: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Modal size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Whether clicking backdrop closes modal (default: true) */
  closeOnBackdrop?: boolean;
  /** Whether pressing Escape closes modal (default: true) */
  closeOnEscape?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-4xl h-[80vh]',
};
let openModalCount = 0;
let previousBodyOverflow = '';

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
}: ModalProps) {
  const titleId = useId();
  const subtitleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
    return () => {
      previousFocus.current?.focus();
      previousFocus.current = null;
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const eventTarget = e.target instanceof Element ? e.target.closest<HTMLElement>('[role="dialog"]') : null;
      if (eventTarget && eventTarget.dataset.modalId !== titleId) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key !== 'Tab') return;
      const dialog = document.querySelector<HTMLElement>(`[data-modal-id="${titleId}"]`);
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((element) => !element.hasAttribute('disabled'));
      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, titleId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      if (openModalCount === 0) previousBodyOverflow = document.body.style.overflow;
      openModalCount += 1;
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (!isOpen) return;
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isFullSize = size === 'full';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0" 
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      
      {/* Modal content */}
      <div 
        className={`relative bg-white rounded-xl ${sizeClasses[size]} w-full ${isFullSize ? 'flex flex-col' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        data-modal-id={titleId}
        tabIndex={-1}
      >
        {/* Header */}
        <div className={`p-6 border-b border-gray-200 ${isFullSize ? 'flex-shrink-0' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 id={titleId} className="text-xl font-semibold text-gray-900">
                {title}
              </h2>
              {subtitle && (
                <p id={subtitleId} className="text-sm text-gray-600 mt-1">{subtitle}</p>
              )}
            </div>
            <button
              ref={closeButtonRef}
              onClick={() => onCloseRef.current()}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`p-6 ${isFullSize ? 'overflow-y-auto flex-1' : ''}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={`p-6 border-t border-gray-200 bg-gray-50 ${isFullSize ? 'flex-shrink-0' : ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
